import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, LifeBuoy, Loader2, Save, Trash2 } from "lucide-react";
import ApprovalsList from "../components/inbox/ApprovalsList";
import { toast } from "sonner";
import AppLayout from "../components/AppLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import TabFeed from "../components/TabFeed";
import SavClosure from "../components/sav/SavClosure";
import SavDesinstDetail from "../components/sav/SavDesinstDetail";
import { PeopleSelects, SAV_CLIENT, SavFieldsGrid, fmtDate, useSavOptions } from "../components/sav/SavFields";

function Card({ title, children, action, testid }) {
  return (
    <section className="card-flat p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3"><h3 className="font-heading text-base font-medium">{title}</h3>{action}</div>
      {children}
    </section>
  );
}

function ClientCard({ client, set }) {
  const cls = "w-full px-2 py-1.5 text-sm border border-norea-border rounded bg-white focus:ring-1 focus:ring-norea-primary outline-none";
  return (
    <div className="grid md:grid-cols-4 gap-3">
      {SAV_CLIENT.map(([k, l]) => (
        <label key={k} className={`block space-y-1 ${k === "adresse" ? "md:col-span-2" : ""}`}><span className="text-[11px] text-norea-textMuted">{l}</span>
          <input value={client[k] || ""} onChange={(e) => set(k, e.target.value)} className={cls} data-testid={`sav-client-${k}`} /></label>
      ))}
    </div>
  );
}

export default function SavDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const opts = useSavOptions();
  const [sav, setSav] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedKey, setFeedKey] = useState(0);
  const [alert, setAlert] = useState(null);
  const [apKey, setApKey] = useState(0);
  const prevCount = useRef(null);
  const load = () => api.get(`/sav/${id}`).then((r) => { setSav(r.data); setDraft({ client: { ...r.data.client }, values: { ...r.data.values }, subcontractor_id: r.data.subcontractor_id, employee_id: r.data.employee_id }); setFeedKey((k) => k + 1); })
    .catch(() => toast.error("Demande SAV introuvable"));
  const loadAlert = () => api.get("/sav/alerts").then((r) => setAlert(r.data.items.find((a) => a.sav_id === id) || null)).catch(() => {});
  useEffect(() => { load(); loadAlert(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!sav || !draft || !opts) return <AppLayout><div className="p-8"><Loader2 className="w-5 h-5 animate-spin" /></div></AppLayout>;
  const remove = async () => {
    if (!window.confirm(`Supprimer la demande ${sav.reference} ?`)) return;
    try { await api.delete(`/sav/${id}`); toast.success("Demande supprimée"); nav("/sav"); } catch (e) { toast.error(e?.response?.data?.detail || "Suppression impossible"); }
  };
  if (sav.kind === "desinstallation") {
    return <AppLayout><SavDesinstDetail sav={sav} statuses={opts.desinst_statuses || []} onReload={load} canDelete={user?.role === "admin"} onDelete={remove} /></AppLayout>;
  }
  const dirty = JSON.stringify(draft) !== JSON.stringify({ client: sav.client, values: sav.values, subcontractor_id: sav.subcontractor_id, employee_id: sav.employee_id });
  const save = async () => {
    setBusy(true);
    try { await api.patch(`/sav/${id}`, draft); toast.success("Demande enregistrée"); load(); loadAlert(); setApKey((k) => k + 1); } catch (e) { toast.error(e?.response?.data?.detail || "Enregistrement impossible"); }
    finally { setBusy(false); }
  };
  const setStatus = async (status) => {
    try { const { data } = await api.post(`/sav/${id}/status`, { status }); setSav((x) => ({ ...x, ...data })); setFeedKey((k) => k + 1); setApKey((k) => k + 1); loadAlert(); toast.success("Statut mis à jour"); } catch (e) { toast.error(e?.response?.data?.detail || "Changement impossible"); }
  };
  return (
    <AppLayout>
      <div className="px-8 py-6 max-w-6xl space-y-5" data-testid="sav-detail-page">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs text-norea-textMuted"><Link to="/sav" className="hover:underline">Service Après-Vente</Link> / <Link to={`/dossiers/${sav.dossier_id}`} className="hover:underline" data-testid="sav-dossier-link">{sav.dossier?.label}</Link></div>
            <h1 className="font-heading text-3xl font-medium text-norea-text flex items-center gap-2"><LifeBuoy className="w-6 h-6 text-norea-primary" /><span data-testid="sav-reference">{sav.reference}</span></h1>
            <div className="text-xs text-norea-textMuted mt-1">{draft.values.type_sav || "—"} · {draft.values.probleme_materiel || "matériel non précisé"} · ouvert le {fmtDate(sav.created_at)}</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={sav.status} onChange={(e) => setStatus(e.target.value)} className="px-2.5 py-1.5 text-sm border border-norea-border rounded bg-white" data-testid="sav-status-select">
              {opts.statuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
            <button onClick={save} disabled={!dirty || busy} className="btn-primary text-sm" data-testid="sav-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Enregistrer</button>
            {user?.role === "admin" && <button onClick={remove} className="btn-ghost text-xs" data-testid="sav-delete"><Trash2 className="w-3.5 h-3.5" /></button>}
          </div>
        </div>
        {alert && (
          <div className="flex items-center gap-3 px-4 py-3 border-l-4 bg-status-erreurBg border-status-erreurText" data-testid="sav-alert">
            <AlertTriangle className="w-4 h-4" /><span className="text-sm font-medium flex-1">{alert.message} — 3 relances sans réponse. Choisissez un nouveau sous-traitant dans « Suivi » puis Enregistrer : l'email d'ouverture lui sera préparé.</span>
          </div>
        )}
        <Card title="Emails SAV à approuver" testid="sav-approvals-card">
          <ApprovalsList savId={sav.id} refreshKey={apKey} onCount={() => { const n = JSON.stringify(sav.relances); if (prevCount.current !== null) load(); prevCount.current = n; }} />
        </Card>
        <Card title="Client" testid="sav-client-card"><ClientCard client={draft.client} set={(k, v) => setDraft((d) => ({ ...d, client: { ...d.client, [k]: v } }))} /></Card>
        <Card title="Suivi" testid="sav-people-card">
          <PeopleSelects opts={opts} sub={draft.subcontractor_id} emp={draft.employee_id} onSub={(v) => setDraft((d) => ({ ...d, subcontractor_id: v }))} onEmp={(v) => setDraft((d) => ({ ...d, employee_id: v }))} />
        </Card>
        <Card title="Demande" testid="sav-fields-card" action={<Link to="/configuration/schemas?tab=sav" className="text-[11px] text-norea-textMuted hover:underline">Paramétrer les champs</Link>}>
          <SavFieldsGrid fields={opts.fields} values={draft.values} onChange={(k, v) => setDraft((d) => ({ ...d, values: { ...d.values, [k]: v } }))} />
        </Card>
        <Card title="Relances" testid="sav-relances-card">
          <div className="flex gap-8 text-sm">
            <div><div className="text-[11px] text-norea-textMuted">Relances faites</div><div data-testid="sav-relances-count">{sav.relances?.count ?? 0}</div></div>
            <div><div className="text-[11px] text-norea-textMuted">Prochaine relance</div><div data-testid="sav-relances-next">{sav.relances?.next_at ? new Date(sav.relances.next_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</div></div>
            <div><div className="text-[11px] text-norea-textMuted">Dernier envoi au sous-traitant</div><div data-testid="sav-relances-last">{sav.relances?.last_sent_at ? new Date(sav.relances.last_sent_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "—"}</div></div>
            <div className="text-[11px] text-norea-textMuted self-end max-w-sm">Relance préparée dans « À approuver » après le délai sans réponse (Ouverture / En cours). Remise à zéro si le sous-traitant répond ou si le statut change ; annulée si une date de RDV est saisie.</div>
          </div>
        </Card>
        <SavClosure sav={sav} kinds={opts.file_kinds} onChanged={load} />
        <TabFeed dossierId={sav.dossier_id} tab="sav" savId={sav.id} refreshKey={feedKey} title="Commentaires et historique" />
      </div>
    </AppLayout>
  );
}
